import createRevenueCatPurchase from "@helpers/createRevenueCatPurchase"
import { trackSubscriptionEvent } from "@helpers/facebookTracking"
import { isNetworkError } from "@helpers/sentryFilter"
import { isPendingPaymentIntentStatus } from "@helpers/stripeErrors"
import {
  createStripeSubscriptionSchedule,
  getUser,
  setRevenueCatCustomerEmail,
} from "@helpers/supabase"
import * as Sentry from "@sentry/react"
import { loadStripe, PaymentIntent } from "@stripe/stripe-js"
import { defer } from "react-router-dom"

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
)

export default function completePaymentLoader({
  request,
}: {
  request: Request
}) {
  const completePaymentPromise = completePayment(request)

  return defer({
    completed: completePaymentPromise,
  })
}

/**
 * `true` once the purchase is recorded, `false` when the payment didn't go
 * through (back to the payment page), `"pending"` while the bank is still
 * confirming it (the payment page waits for it), or the sign-in page to
 * finish on.
 */
export type CompletePaymentResult = "pending" | boolean | { signInPath: string }

export async function completePayment(
  request: Request,
): Promise<CompletePaymentResult> {
  const url = new URL(request.url)

  const clientSecret = url.searchParams.get("payment_intent_client_secret")
  const stripeFirstPriceId = url.searchParams.get("price_id")!
  const stripeNextPriceId = url.searchParams.get("next_price_id")!
  const subscriptionId = url.searchParams.get("subscription_id")

  if (!clientSecret) return false

  const paymentIntent = await retrieveStripePaymentIntent(clientSecret)

  if (isPendingPaymentIntentStatus(paymentIntent?.status)) return "pending"

  if (!paymentIntent || paymentIntent.status !== "succeeded") {
    return false
  }

  if (!subscriptionId) return false

  const userId = await getSupabaseUserId()

  // NOTE: Paid, but signed out: a payment method that redirects (e.g. PayPal)
  // came back with a page load, and a browser that blocks storage kept the
  // session in memory only. Signing in comes back here to finish.
  if (!userId) {
    return {
      signInPath: `/account/sign-in?${new URLSearchParams({
        path: `${url.pathname}${url.search}`,
      }).toString()}`,
    }
  }

  await createRevenueCatPurchase({
    subscriptionId: subscriptionId,
    userId: userId,
  })

  // NOTE: Paid and recorded by now, so the steps below must not turn the
  // success page into an error page. A missing schedule only delays the move
  // to the full price, and the backend's ensureIntroPriceSchedule repairs it.
  await bestEffort(() => createStripeSubscriptionSchedule({ subscriptionId }))

  await bestEffort(() =>
    trackSubscriptionEvent({
      currency: paymentIntent.currency,
      isPurchase: true,
      planPrice: paymentIntent.amount,
      stripeFirstPriceId,
      stripeNextPriceId,
    }),
  )

  await bestEffort(() => setRevenueCatCustomerEmail())

  return true
}

async function bestEffort(step: () => Promise<unknown>): Promise<void> {
  try {
    await step()
  } catch (error) {
    if (!isNetworkError(error)) Sentry.captureException(error)
  }
}

async function getSupabaseUserId(): Promise<string | undefined> {
  const { data } = await getUser()

  const userId = data?.user?.id

  return userId
}

async function retrieveStripePaymentIntent(
  clientSecret: string,
): Promise<PaymentIntent | undefined> {
  const stripe = await stripePromise

  const response = await stripe?.retrievePaymentIntent(clientSecret)

  return response?.paymentIntent
}
